import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge, Spinner, Table } from 'react-bootstrap';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import Paginacao from '../../components/Paginacao';
import BuscaAvancada from '../../components/BuscaAvancada';

const CAMPOS_BUSCA = [
  { nome: 'nome', rotulo: 'Nome', tipo: 'text' },
  { nome: 'email', rotulo: 'E-mail', tipo: 'text' },
  { nome: 'telefone', rotulo: 'Telefone', tipo: 'text' },
  { nome: 'interesse', rotulo: 'Imóvel de interesse', tipo: 'text' },
  { nome: 'cidade', rotulo: 'Cidade', tipo: 'text' },
  { nome: 'bairro', rotulo: 'Bairro', tipo: 'text' },
  { nome: 'origem', rotulo: 'Origem (mídia/referência)', tipo: 'text' },
  {
    nome: 'situacao',
    rotulo: 'Situação',
    tipo: 'select',
    opcoes: [
      { valor: 'novos', rotulo: 'Novos' },
      { valor: 'integrados', rotulo: 'Integrados a cliente' },
    ],
  },
  { nome: 'criado_de', rotulo: 'Recebido a partir de', tipo: 'date' },
  { nome: 'criado_ate', rotulo: 'Recebido até', tipo: 'date' },
];

// Sem criar/editar — lead vem de fora (site, portais). Só listar e ver
// detalhe (com o botão "Efetivar em cliente", ver PaginaLead.js).
export default function ListaLeads() {
  const toast = useToast();
  const { usuario } = useAuth();
  // `?q=` na URL (link "ver todos" da busca geral do Dashboard) já chega filtrado — ver mesmo
  // comentário em ListaClientes.js.
  const [searchParams] = useSearchParams();
  const [qInicial] = useState(() => searchParams.get('q') || '');
  const [empresas, setEmpresas] = useState([]);
  const [filtros, setFiltros] = useState(() => (qInicial ? { q: qInicial } : {}));
  const [leads, setLeads] = useState(null);
  const [meta, setMeta] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [erro, setErro] = useState(false);

  // nivel 1 (superadmin) enxerga todas as empresas — ganha um filtro por empresa
  useEffect(() => {
    if (usuario.nivel === 1) api.get('/empresas').then((r) => setEmpresas(r.data)).catch(() => {});
  }, [usuario.nivel]);

  useEffect(() => {
    let cancelado = false;
    setErro(false);
    // filtros da busca (simples `q` + avançados) viajam como query string — o backend valida/limita
    const query = new URLSearchParams({ page: pagina, per_page: perPage, ...filtros });
    api
      .get(`/leads?${query}`)
      .then((r) => {
        if (cancelado) return;
        setLeads(r.data);
        setMeta(r.meta);
      })
      .catch((err) => {
        if (!cancelado) {
          setErro(true);
          toast.erro(err.message || 'Não foi possível carregar os leads');
        }
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, perPage, filtros]);

  function aoBuscar(novosFiltros) {
    setFiltros(novosFiltros);
    setPagina(1);
  }

  const campos =
    usuario.nivel === 1
      ? [
          {
            nome: 'empresa_id',
            rotulo: 'Empresa',
            tipo: 'select',
            opcoes: empresas.map((e) => ({ valor: e.public_id, rotulo: e.e1_nome })),
          },
          ...CAMPOS_BUSCA,
        ]
      : CAMPOS_BUSCA;

  function aoMudarPerPage(novoPerPage) {
    setPerPage(novoPerPage);
    setPagina(1);
  }

  return (
    <>
      <h1 className="h4 mb-4">Leads</h1>

      <BuscaAvancada
        campos={campos}
        placeholderSimples="Buscar por nome, e-mail, telefone, imóvel ou cidade"
        aoBuscar={aoBuscar}
        initialQ={qInicial}
      />

      {!leads && !erro && <Spinner animation="border" />}

      {leads && (
        <>
          <Table hover responsive>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Telefone</th>
                <th>Imóvel de interesse</th>
                <th>Cidade</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.public_id}>
                  <td>
                    <Link to={`/leads/${lead.public_id}`}>{lead.l1_nome || '(sem nome)'}</Link>
                  </td>
                  <td>{lead.l1_telefone}</td>
                  <td>{lead.l1_tituloimovel || lead.l1_tipoimovel}</td>
                  <td>{lead.l1_cidade}</td>
                  <td>
                    {lead.l1_integrado === 'integrado' ? (
                      <Badge bg="success">Integrado</Badge>
                    ) : (
                      <Badge bg="secondary">Novo</Badge>
                    )}
                  </td>
                </tr>
              ))}
              {leads.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    {Object.keys(filtros).length > 0 ? 'Nenhum lead encontrado com esses filtros.' : 'Nenhum lead ainda.'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <Paginacao meta={meta} aoMudarPagina={setPagina} aoMudarPerPage={aoMudarPerPage} />
        </>
      )}
    </>
  );
}
